import { ChevronRight, KeyRound, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type { TreasurePrefs } from "@/lib/schemas";
import {
  TREASURE_KEYS_PER_CHEST,
  TREASURE_REWARDS,
  type TreasureProgress,
} from "@/lib/utils/treasure";

export function TreasureMeadowCard({
  progress,
  treasure,
  onOpenChest,
  onOpenCollection,
}: {
  progress: TreasureProgress;
  treasure: TreasurePrefs;
  onOpenChest: () => void;
  onOpenCollection: () => void;
}) {
  const collectionComplete =
    treasure.ownedRewardIds.length >= TREASURE_REWARDS.length;
  const chestReady = progress.availableChestCount > 0 && !collectionComplete;
  const equippedCount = Object.keys(treasure.equipped ?? {}).length;
  const filledKeys = chestReady
    ? TREASURE_KEYS_PER_CHEST
    : progress.keysTowardNextChest;

  return (
    <section className="relative overflow-hidden rounded-[22px] border border-[#C9DFBC] bg-[linear-gradient(135deg,#F4F8E8_0%,#E1F0CD_100%)] px-4 py-3.5 shadow-sm">
      <div className="pointer-events-none absolute -right-5 -top-7 h-28 w-28 rounded-full bg-white/35 blur-xl" />
      <div className="relative z-[1] max-w-[68%]">
        <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-forest/75">
          <Sparkles className="h-3.5 w-3.5" />
          Polana Sianka
        </div>
        <h2 className="mt-0.5 font-display text-[19px] font-extrabold leading-tight text-navy">
          {collectionComplete
            ? "Cała kolekcja zdobyta!"
            : chestReady
              ? "Skrzynka jest gotowa!"
              : "Zbieraj klucze do skrzynki"}
        </h2>
        <p className="mt-1 text-[12px] leading-snug text-slate">
          {collectionComplete
            ? "Wszystkie skarby z Polany są już Twoje. Możesz je dowolnie zmieniać."
            : chestReady
              ? "W środku czeka jeden losowy gadżet, którego jeszcze nie masz."
              : "Pierwsze zatwierdzone zdjęcie zadania dnia daje jeden klucz."}
        </p>

        <div
          className="mt-2.5 flex items-center gap-1.5"
          aria-label={`${filledKeys} z ${TREASURE_KEYS_PER_CHEST} kluczy`}
        >
          {Array.from({ length: TREASURE_KEYS_PER_CHEST }, (_, index) => (
            <span
              key={index}
              className={`grid h-7 w-7 place-items-center rounded-full border transition-colors ${
                index < filledKeys
                  ? "border-gold bg-sunny text-[#7A5A00] shadow-[0_2px_0_#D8A824]"
                  : "border-white/80 bg-white/55 text-slate/35"
              }`}
            >
              <KeyRound className="h-3.5 w-3.5" />
            </span>
          ))}
          {!chestReady && !collectionComplete && (
            <span className="ml-1 text-[11px] font-semibold text-forest">
              Jeszcze {progress.keysNeededForNextChest}
            </span>
          )}
        </div>

        <div className="mt-3 flex items-center gap-2">
          {chestReady ? (
            <Button size="sm" variant="sunny" onClick={onOpenChest}>
              Otwórz skrzynkę
            </Button>
          ) : (
            <button
              type="button"
              onClick={onOpenCollection}
              className="inline-flex items-center gap-1 text-[12px] font-bold text-forest hover:text-green"
            >
              Kolekcja {treasure.ownedRewardIds.length}/
              {TREASURE_REWARDS.length}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {equippedCount > 0 && (
          <button
            type="button"
            onClick={onOpenCollection}
            className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full bg-white/70 px-2 py-1 text-[10px] font-bold text-navy ring-1 ring-white"
          >
            <Sparkles className="h-3.5 w-3.5 text-green" />
            <span className="truncate">
              Bohater ma {equippedCount}{" "}
              {equippedCount === 1
                ? "gadżet"
                : equippedCount >= 2 && equippedCount <= 4
                  ? "gadżety"
                  : "gadżetów"}
            </span>
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={chestReady ? onOpenChest : onOpenCollection}
        className={`absolute -right-2 bottom-0 z-[1] grid h-[128px] w-[140px] place-items-end pr-2 transition-transform hover:scale-[1.03] ${
          chestReady ? "treasure-chest-ready" : "opacity-85"
        }`}
        aria-label={chestReady ? "Otwórz skrzynkę" : "Otwórz kolekcję skarbów"}
      >
        <img
          src="/treasure/chest.webp?v=1"
          alt=""
          className="h-auto w-[132px] select-none drop-shadow-[0_12px_14px_rgba(94,72,24,0.22)]"
          draggable={false}
        />
      </button>
    </section>
  );
}
